// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// What this browser has already been shown, and already been asked (M3-32, M3-34, 08 §5.2).
//
// "Flipper zones are not drawn as buttons during play — a real cabinet has no on-screen buttons —
// but the zone boundary glows for the first ball of a player's first game, then fades away
// permanently."
//
// Two halves, and the second is the one that needs storing. *Not drawn as buttons* is the whole
// aesthetic argument for the touch layout: the moment there are two grey rectangles at the bottom
// of the screen it stops being a pinball machine and becomes an app with buttons. *Glows once* is
// the concession to the fact that an invisible control is an undiscoverable one.
//
// So it is shown exactly once per browser, and "permanently" is meant: there is no setting to
// bring it back, because a player who has flipped a flipper knows where the flippers are.
//
// The device-motion offer (M3-34) is the same shape of question and so it lives here too, with
// one difference that matters: the zone hint is a *hint* and forgetting it costs nothing, while
// the motion answer is a **decision about a control**. So it is not one-way. "Offered once"
// means the machine asks once and then stops asking — it does not mean a player who said no on
// their first ball is stuck with that forever, which is why Settings carries the same choice.

import { readLocal, removeLocal, safeStorage, writeLocal, type StorageLike } from '../storage/local'
// The alphabet and the default come from the core, which is the only place that decides what a
// character position may hold. A second copy here would be a second opinion that drifts.
import { DEFAULT_INITIALS, INITIALS_ALPHABET } from '@core/game'

/** One key per hint. Prefixed like every other key this product owns. */
export const TOUCH_ZONES_KEY = 'hcc.pinball.seen-touch-zones'

/**
 * Whether this browser has ever actually pulled the plunger (M3-51).
 *
 * **Its own key, deliberately, and that is the point of it.** The zone hint used to retire both
 * halves of the lesson at once — a player who tapped a flipper on ball one lost the plunger arrow
 * they had never been shown. The two gestures are learned independently: a thumb finds the
 * flippers in seconds and the plunger's *drag* is the one thing on this machine nobody guesses.
 */
export const PLUNGER_PULLED_KEY = 'hcc.pinball.pulled-plunger'

/** The device-motion answer, `'on'` or `'off'`. Absent means never asked. */
export const MOTION_NUDGE_KEY = 'hcc.pinball.motion-nudge'

/**
 * Whether the key guide is open, `'open'` or `'closed'`. Absent means never touched (M3-56).
 *
 * **The only one of these keys that stores a choice rather than a lesson.** The two above are
 * facts about what a browser has been shown, and the product decides what to do with them; this is
 * the player pressing a button that says "hide this", and a product that reopened a panel somebody
 * closed would be overruling them once a session.
 */
export const KEY_GUIDE_KEY = 'hcc.pinball.key-guide'

/** What the player said about nudging by moving the phone. */
export type MotionChoice = 'on' | 'off'

/**
 * Has this browser been shown the flipper zones?
 *
 * A browser rather than a player, because there is no player yet — an anonymous visitor creates
 * no identity (08 §1), so the most this can know is "this browser has done this before". Once
 * accounts exist (M4) the same flag moves onto the profile and follows them between devices; the
 * shape of the question does not change.
 */
export function hasSeenTouchZones(storage: StorageLike | undefined = safeStorage()): boolean {
  return readLocal(storage, TOUCH_ZONES_KEY) === '1'
}

/** Remember that they have. Silent where storage is unavailable — the hint simply shows again. */
export function markTouchZonesSeen(storage: StorageLike | undefined = safeStorage()): void {
  writeLocal(storage, TOUCH_ZONES_KEY, '1')
}

/**
 * Show it again on the next ball (M3-33a).
 *
 * **"Permanently" was too strong, and the module beside it already knew why.** The paragraph above
 * argues that a player who has flipped a flipper knows where the flippers are — which is true of
 * that player, on that day. It is not true of the same person a month later, of the friend they
 * hand the phone to, or of anybody whose hint was spent on a stray tap before the flipper-press
 * fix. And the cost of being wrong is total: this is the *only* thing that ever says where the
 * flipper zones are, because 08 §5.2 forbids drawing them as buttons.
 *
 * The device-motion answer three lines down made exactly this concession — *"it does not mean a
 * player who said no on their first ball is stuck with that forever, which is why Settings carries
 * the same choice"* — and the same reasoning was never applied here. It is now.
 *
 * Deliberately a *forget* rather than a `shown` flag: there is one fact, "has this browser been
 * shown the zones", and the way to see them again is for that to become false. A second flag
 * meaning "show them anyway" would be a second answer to one question.
 */
export function forgetTouchZones(storage: StorageLike | undefined = safeStorage()): void {
  removeLocal(storage, TOUCH_ZONES_KEY)
  // The plunger arrow goes with it. Settings offers one control — "show me again" — and a player
  // who asks for the flipper zones back and gets everything *except* the one gesture nobody
  // guesses has been given the easy half of what they asked for.
  removeLocal(storage, PLUNGER_PULLED_KEY)
}

/** Has this browser pulled the plunger properly, at least once? */
export function hasPulledPlunger(storage: StorageLike | undefined = safeStorage()): boolean {
  return readLocal(storage, PLUNGER_PULLED_KEY) === '1'
}

/** Remember that it has. Silent where storage is unavailable — the arrow simply shows again. */
export function markPlungerPulled(storage: StorageLike | undefined = safeStorage()): void {
  writeLocal(storage, PLUNGER_PULLED_KEY, '1')
}

/**
 * What the player said about device-motion nudging, or nothing if they were never asked.
 *
 * **Deliberately not the platform's answer.** iOS keeps its own permission per origin and will
 * hand it back without a prompt; this is our side of the same conversation, and the two can
 * disagree in both directions — a player who declined our offer on a browser that never needed a
 * system prompt, or one who granted iOS the sensor and then turned the control off in Settings.
 * The product honours this one, because this one is the one the player set on purpose.
 */
export function readMotionChoice(
  storage: StorageLike | undefined = safeStorage(),
): MotionChoice | undefined {
  const stored = readLocal(storage, MOTION_NUDGE_KEY)
  return stored === 'on' || stored === 'off' ? stored : undefined
}

/** Remember it. Silent where storage is unavailable — the offer simply comes back next time. */
export function writeMotionChoice(
  choice: MotionChoice,
  storage: StorageLike | undefined = safeStorage(),
): void {
  writeLocal(storage, MOTION_NUDGE_KEY, choice)
}

/** Open or collapsed, as the player last left it. */
export type GuideChoice = 'open' | 'closed'

/**
 * What the player last chose about the key guide, or nothing if they never have.
 *
 * The absent case is the interesting one and `keyGuideStartsOpen` is where it is answered.
 */
export function readKeyGuideChoice(
  storage: StorageLike | undefined = safeStorage(),
): GuideChoice | undefined {
  const stored = readLocal(storage, KEY_GUIDE_KEY)
  return stored === 'open' || stored === 'closed' ? stored : undefined
}

/** Remember it. Silent where storage is unavailable — the panel simply opens again next time. */
export function writeKeyGuideChoice(
  choice: GuideChoice,
  storage: StorageLike | undefined = safeStorage(),
): void {
  writeLocal(storage, KEY_GUIDE_KEY, choice)
}

/**
 * Open, unless this browser has said otherwise.
 *
 * **Open on a first visit, because a legend nobody opens teaches nobody anything.** The panel is
 * the only place the plunger's hold is written down, and a collapsed tab labelled "Controls" is a
 * thing a player who does not yet know they are missing anything has no reason to press.
 *
 * **And then never reopened by us.** The touch zone hint retires itself after one ball because a
 * *gesture* is muscle memory and a hint that outstays that is clutter. A key mapping is recall, and
 * recall decays — somebody coming back after a month genuinely will not remember that nudging is
 * `W A S D` — so this one retires when the player says so and not before, and it comes back the
 * same way. That is also why it is stored as a choice and not as a `seen` flag: there is one
 * question here, "is it open", and a second flag meaning "show it anyway" would be a second answer
 * to it.
 */
export function keyGuideStartsOpen(storage: StorageLike | undefined = safeStorage()): boolean {
  return (readKeyGuideChoice(storage) ?? 'open') === 'open'
}

/**
 * Whether the player last chose focus mode, `'on'` or `'off'`. Absent means never touched.
 *
 * **The second key here that stores a choice rather than a lesson**, and it is stored for the
 * reason the key guide's is: focus mode is a button the player pressed, and a product that gave
 * the bars back every visit would be overruling them once a session.
 *
 * **A choice, emphatically not the state.** The two come apart the moment a browser refuses
 * fullscreen — see `playFocus.ts`, where the rows collapse whatever the browser allows — so what
 * is written down is "this player wants the machine to have the screen", never "the machine had
 * the screen when we last looked". The second reading would persist an accident.
 */
export const PLAY_FOCUS_KEY = 'hcc.pinball.play-focus'

/** What the player last chose about focus mode, or nothing if they never have. */
export function readFocusChoice(
  storage: StorageLike | undefined = safeStorage(),
): FocusChoice | undefined {
  const stored = readLocal(storage, PLAY_FOCUS_KEY)
  return stored === 'on' || stored === 'off' ? stored : undefined
}

/** On, or off, as the player last left it. */
export type FocusChoice = 'on' | 'off'

/** Remember it. Silent where storage is unavailable — the bars simply come back next time. */
export function writeFocusChoice(
  choice: FocusChoice,
  storage: StorageLike | undefined = safeStorage(),
): void {
  writeLocal(storage, PLAY_FOCUS_KEY, choice)
}

/**
 * Should the machine take the screen on arrival?
 *
 * **Only for somebody who has asked for it before, and that is the whole rule.** Focus mode
 * removes the top bar and the navigation from the tree, so a first-time visitor started inside it
 * would land on a machine with no logo, no Arcade, no Boards and no Build, and the way back out is
 * a row in a menu drawn over the playfield — discoverable for a player who chose this and
 * invisible to one who never did. An absent answer therefore means *no*, unlike the key guide,
 * where an absent answer means *yes*: a legend nobody opens teaches nobody anything, and a
 * navigation nobody can find is a product with no way through it.
 *
 * **The fullscreen half cannot come with it**, and no amount of arranging here would change that:
 * `requestFullscreen` needs a user gesture and a mount is not one. It arrives on the first pull of
 * the plunger instead — see `MachineStage`'s `onPlunge` — which is the first gesture on the
 * critical path and usually about a second away.
 */
export function focusStartsOn(storage: StorageLike | undefined = safeStorage()): boolean {
  return readFocusChoice(storage) === 'on'
}

/**
 * The last three characters this player put on a table (M2-19a).
 *
 * **A convenience, and never an identity.** It is not a name, it is not synced and it is not
 * attached to anybody: three characters in one browser, so that somebody who plays this machine
 * twice a week does not spin the same wheels to the same place every time. Which is also why it is
 * written only for initials a player actually *committed* — a skip means "do not put my name on
 * it", and remembering the letters the wheels happened to be showing when they said so would be
 * remembering the opposite of what they chose.
 *
 * **Not in `preferences.ts`.** It is a fact about this browser, like the first-run hints beside it,
 * and carrying it between devices would put one person's initials on a machine somebody else is
 * sitting at.
 */
export const LAST_INITIALS_KEY = 'hcc.pinball.last-initials'

/**
 * What this browser last committed, or nothing.
 *
 * Validated against the machine's own alphabet rather than a second copy of it: a value that is
 * not three enterable characters is not something this build wrote, and `Game` would silently
 * resolve it to 'AAA' anyway. Refusing it here means the settings a player sees and the wheels
 * they get agree.
 */
export function readLastInitials(
  storage: StorageLike | undefined = safeStorage(),
): string | undefined {
  const stored = readLocal(storage, LAST_INITIALS_KEY)
  if (stored === undefined || stored.length !== 3) return undefined
  // `<` is rubout — a control rather than a character, so it is not somewhere to start.
  for (const character of stored) {
    if (character === '<' || !INITIALS_ALPHABET.includes(character)) return undefined
  }
  return stored
}

/** Remember three committed characters. Silent where storage is unavailable. */
export function writeLastInitials(
  initials: string,
  storage: StorageLike | undefined = safeStorage(),
): void {
  writeLocal(storage, LAST_INITIALS_KEY, initials)
}

/**
 * Where a fresh game's initials entry should open.
 *
 * `undefined` rather than `DEFAULT_INITIALS` for a browser that has never committed any, because
 * the trace omits the field at its default — see `ReplayTrace.startingInitials`. Handing 'AAA'
 * down explicitly would put a header on every trace that says exactly what its absence already
 * says, and change the submission key of every game that never used the feature.
 */
export function startingInitialsFor(
  storage: StorageLike | undefined = safeStorage(),
): string | undefined {
  const last = readLastInitials(storage)
  return last === undefined || last === DEFAULT_INITIALS ? undefined : last
}
